import { Injectable } from 'angular-ts-decorators';

import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';

import { isEmpty, map, omitBy, lowerCase } from 'lodash';

/**
 * Simple service that allows the retrieving and updating of settings
 *
 * Please compare with {@link sharedModules.settingsManager}.
 */
@Injectable('settings')
export class Settings {
  private url: string;
  constructor(
    private $q: ng.IQService,
    private $http: ng.IHttpService,
    domain: Domain,
    config: IConfig,
  ) {
    'ngInject';

    this.url = '//' + domain.createSibling(config.spynlSubdomain);
  }

  /**
   *
   * Returns a object that contains the user and tenant settings
   * @returns {httpPromise} resolve with a object containing
   * user and tenant settings
   */
  get(): ng.IPromise<any> {
    return this.$http.get(this.url + '/settings/get').then(
      (response: any) => response.data.data,
      (error) => this.$q.reject(error.data.message),
    );
  }

  /**
   *
   * Returns a object that contains the current tenant settings
   * @param {string} tenantId
   * Id of a tenant
   * @returns {httpPromise} resolve with a object containing
   * tenant settings
   */
  getTenant(tenantId: string): ng.IPromise<any> {
    return this.$http
      .get(this.url + '/tenants/' + tenantId + '/settings/get-tenant')
      .then(
        (response: any) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Returns a object that contains the current user settings
   * @param {string} userName
   * Optional parameter, to choose the user to be read. If not provided, spynl
   * default to the current user.
   * @returns {httpPromise} resolve with a object containing
   * user settings
   */
  getUser(userName?: string): ng.IPromise<any> {
    return this.$http
      .get(this.url + '/settings/get-user', {
        params: {
          username: userName,
        },
      })
      .then(
        (response: any) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * Let you set the tenant settings. Note! It precess the data before
   * sending it to the backend.
   * @param {string} tenantId
   * Id of a tenant
   * @param {object} newSettings
   * A object containing the tenant settings
   * @returns {httpPromise} resolve with a object containing
   * tenant settings
   */
  setTenant(tenantId: string, newSettings: any): ng.IPromise<any> {
    // removes all empty keys - will break down when some setting will
    // have '' as a valid value
    const cleanedSettings = omitBy(newSettings, (value) => {
      if (angular.isArray(value)) {
        return false;
      } else if (angular.isObject(value)) {
        return isEmpty(value);
      } else {
        return false;
      }
    });

    if (angular.isArray(cleanedSettings.shippingCarriers)) {
      cleanedSettings.shippingCarriers = map(
        cleanedSettings.shippingCarriers,
        lowerCase,
      );
    }

    return this.$http
      .post(this.url + '/tenants/' + tenantId + '/settings/set-tenant', {
        settings: cleanedSettings,
      })
      .then(
        (response: any) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * Let you unset the tenant settings.
   * @param {string} tenantId
   * Id of a tenant
   * @param {string[]} keys
   * Key of settings to be removed
   * @returns {httpPromise} resolve with a object containing
   * tenant settings
   */
  deleteTenant(keys: string[], tenantId?: string): ng.IPromise<any> {
    let prefix = '';

    if (tenantId) {
      prefix = '/tenants/' + tenantId;
    }

    return this.$http
      .post(`${this.url}${prefix}/settings/delete-tenant`, {
        settings: keys,
      })
      .then((response: any) => response.data.data);
  }

  /**
   * Let you set the user settings
   * @param {object} settings
   * A object containing the user settings
   * @param {string} userName
   * Optional parameter, to choose the user to be changed. If not provided,
   * spynl default to the current user.
   * @returns {httpPromise} resolve with a object containing
   * user settings
   */
  setUser(settings: any, userName?: string): ng.IPromise<any> {
    return this.$http
      .post(
        this.url + '/settings/set-user',
        {
          settings,
        },
        {
          params: {
            username: userName,
          },
        },
      )
      .then(
        (response: any) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * Let you unset the user's settings
   * @param {object} keys
   * Array of keys to be removed.
   * @param {string} userName
   * Optional parameter, to choose the user to be changed. If not provided,
   * spynl default to the current user.
   * @returns {httpPromise} resolve with a object containing
   * user settings
   */
  deleteUser(keys: string[], userName?: string): ng.IPromise<any> {
    return this.$http
      .post(
        this.url + '/settings/delete-user',
        {
          settings: keys,
        },
        {
          params: {
            username: userName,
          },
        },
      )
      .then((response: any) => response.data.data);
  }

  /**
   * Lets you set the upload directory for the given tenant
   * @param {string} tenantId
   * Id of a tenant
   * @param {string} directory
   * The new upload directory
   * @returns {httpPromise} resolves or rejects with a status message
   */
  setUploadDirectory(tenantId: string, directory: string): ng.IPromise<any> {
    return this.$http
      .post(
        `${this.url}/tenants/${tenantId}/account-manager/change-upload-directory`,
        {
          settings: {
            uploadDirectory: directory,
          },
        },
      )
      .then(
        (response: any) => response.data.message,
        (error) => this.$q.reject(error.data.message),
      );
  }
}
